import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import {
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  GlobalFiltering,
  getSortedRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import GlobalFilter from "../../../components/globalFilter";
import ErrorModal from "../../../components/modalError";
import Tabelas from "../../../components/table";
import PropTypes from "prop-types";
import DeleteConfirmationModal from "../../../components/modalDelete";
import CSVButton from "../../../components/CSVButton";
import { useTranslation } from "react-i18next";
import IconToSolve from "../../../components/icon";

function AtributosTable({
  data,
  onShowModal,
  hideButton,
  onDataDeleted,
  deleteData,
  onEditAttribute,
  lockedAttributeIds,
  disableLocking = false,
  onShowResourceModal,
  openModalInfo,
}) {
  const { t } = useTranslation();
  const [error, setError] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [tableData, setTableData] = useState(data || []);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [attrToDeleteId, setAttrToDeleteId] = useState(null);
  const [attrName, setAttrName] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const DATA_TYPE_LABEL = {
    S: t("Texto"),
    N: t("Numérico"),
    B: t("Booleano"),
  };

  const handleDeleteRequest = (row) => {
    setAttrToDeleteId(row.id);
    setAttrName(row.name);
    setShowDeleteConfirmationModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteData(`/attributes/${attrToDeleteId}`);
      setTableData((prev) => prev.filter((item) => item.id !== attrToDeleteId));
      onDataDeleted(attrToDeleteId);
    } catch (err) {
      setError(t("Erro ao deletar esse atributo"));
      console.error(err);
      setShowErrorModal(true);
    }
    setShowDeleteConfirmationModal(false);
    setAttrToDeleteId(null);
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setError("");
  };

  const globalFilter = React.useCallback((rows, ids, query) => {
    const searchTerm = String(query);
    return rows.filter((row) => {
      const matches = ids.filter((id) => {
        const rowValue = row.values[id];
        if (searchTerm === "'") return true;
        if (searchTerm.startsWith("'") && searchTerm.endsWith("'")) {
          const exactSearchTerm = searchTerm.slice(1, -1);
          return String(rowValue) === exactSearchTerm;
        }
        let searchLower = searchTerm;
        if (searchTerm.startsWith("'")) searchLower = searchTerm.slice(1);
        return rowValue !== undefined
          ? String(rowValue).toLowerCase().includes(searchLower.toLowerCase())
          : false;
      });
      return matches.length > 0;
    });
  }, []);

  const exportToCSV = () => {
    if (!data) return;
    return data.map((row) => ({
      Nome: row.name,
      ...(disableLocking ? { Slug: row.slug } : {}),
      Tipo: DATA_TYPE_LABEL[row.data_type] ?? row.data_type,
      Obrigatório: row.is_required ? t("Sim") : t("Não"),
      Recurso: row.resource?.name || "-",
    }));
  };

  const columns = [
    { header: t("Nome"), accessorKey: "name", cell: (info) => info.getValue() },
    ...(disableLocking
      ? [
          {
            header: t("Slug"),
            accessorKey: "slug",
            cell: (info) => info.getValue(),
            id: "slug",
          },
        ]
      : []),
    {
      header: t("Descrição"),
      accessorKey: "description",
      cell: (info) => info.getValue() || "-",
    },
    {
      header: t("Tipo"),
      accessorKey: "data_type",
      cell: (info) => DATA_TYPE_LABEL[info.getValue()] ?? info.getValue(),
    },
    {
      header: t("Obrigatório"),
      accessorKey: "is_required",
      cell: (info) => (info.getValue() ? t("Sim") : t("Não")),
    },
    {
      header: t("Recurso"),
      accessorKey: "resource.name",
      cell: (info) => info.getValue(),
    },
    {
      id: "acoes",
      header: (
        <div className="header-action">
          {hideButton && (
            <>
              <button
                onClick={onShowModal}
                className="btn-table  bi bi-plus-circle"
              ></button>
              <button
                onClick={onShowResourceModal}
                className="btn-table m-0 pe-0 ps-2"
                title={t("Atribuir valores a atributos")}
              >
                <IconToSolve
                  className={"align-bottom "}
                  iconName={"Atributo"}
                  width={32}
                  height={32}
                />
              </button>
            </>
          )}
          <CSVButton data={exportToCSV()} filename={"Atributos.csv"} />
        </div>
      ),
      cell: (info) => {
        const isLocked = lockedAttributeIds?.hasOwnProperty(
          info.row.original.id,
        );

        if (!disableLocking && isLocked) return null;

        return (
          <div className="header-action">
            {hideButton && (
              <>
                <button
                  className="btn-table btn-editar bi bi-pencil-square"
                  onClick={() => onEditAttribute(info.row.original)}
                  title={t("Editar atributo")}
                  disabled={!disableLocking && isLocked}
                />
                <button
                  className="btn-table btn-delete bi bi-trash3"
                  onClick={() => handleDeleteRequest(info.row.original)}
                  title={t("Deletar atributo")}
                  disabled={!disableLocking && isLocked}
                />
              </>
            )}
            <button
              className="btn-table btn-editar bi bi-three-dots"
              onClick={() => openModalInfo(info.row.original)}
              title={t("Mais detalhes")}
            ></button>
          </div>
        );
      },
    },
  ];

  const table = useReactTable(
    {
      data: tableData,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      globalFilter: globalFilter,
      getSortedRowModel: getSortedRowModel(),
      state: { sorting, pagination },
      onSortingChange: setSorting,
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
      getRowProps: (row) => ({
        className: row.original.active ? "" : "inactive-user",
      }),
      defaultColumn: {
        sortDescFirst: false,
      },
    },
    GlobalFiltering,
    getSortedRowModel,
  );

  const { setGlobalFilter } = table;

  return (
    <div className="p-2 div-table">
      <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />

      <Tabelas fixLastColumn={true} table={table} />
      <div className="pagination">
        <Button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          title="pagina anterior"
          className="pagination-btn"
        >
          <i className="bi bi-chevron-left pagination-icon"></i>
        </Button>
        <Button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          title="pagina seguinte"
          className="pagination-btn"
        >
          <i className="bi bi-chevron-right pagination-icon"></i>
        </Button>
        <select
          value={pagination.pageSize}
          onChange={(e) => table.setPageSize(Number(e.target.value))}
          className="pagination-select"
          aria-label="Select de paginação"
          id="select"
        >
          {[5, 10, 20, 30, 40, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              {pageSize}
            </option>
          ))}
        </select>
      </div>
      <ErrorModal
        showErrorModal={showErrorModal}
        handleCloseErrorModal={handleCloseErrorModal}
        errorMessage={error}
      />
      <DeleteConfirmationModal
        acao={t("deletar")}
        show={showDeleteConfirmationModal}
        onClose={() => setShowDeleteConfirmationModal(false)}
        onConfirm={confirmDelete}
        pagina={t("atributo")}
        name={attrName}
        modificacoes={t(" logs")}
      />
    </div>
  );
}

AtributosTable.propTypes = {
  data: PropTypes.array.isRequired,
  onShowModal: PropTypes.func.isRequired,
  hideButton: PropTypes.bool.isRequired,
  onDataDeleted: PropTypes.func.isRequired,
  deleteData: PropTypes.func.isRequired,
  onEditAttribute: PropTypes.func.isRequired,
  onAssignValues: PropTypes.func.isRequired,
  disableLocking: PropTypes.bool,
  onShowResourceModal: PropTypes.func.isRequired,
};

export default AtributosTable;
